/**
 * Theme presets — ported from the shadcn UI kit (`lib/themes.ts` + `themes.css`).
 *
 * Each value matches a `[data-theme-preset="…"]` block in `src/themes.css`, which
 * is the kit's file verbatim. A preset redefines the whole token scale (base,
 * primary, secondary, chart, sidebar), the radius AND the typeface, so switching
 * one is a genuine re-skin rather than an accent-colour swap.
 *
 * `default` is the absence of a preset: the tokens in `styles.css :root` apply
 * and no attribute is set. Every other value must exist in `themes.css` — a
 * typo here degrades silently to the default palette.
 *
 * The kit's floating theme-customizer panel is deliberately NOT ported. Theme
 * selection lives in Settings → Appearance, so the console has one settings
 * surface instead of two competing ones.
 */

/** Displayed swatch colours are each preset's `--primary`, from the kit. */
export const THEME_PRESETS = [
  { value: 'nafis', name: 'NAFIS', swatch: '#2563eb' },
  { value: 'ocean-breeze', name: 'Ocean Breeze', swatch: 'oklch(0.59 0.20 277.12)' },
  { value: 'default', name: 'Default', swatch: 'oklch(0.33 0 0)' },
  { value: 'underground', name: 'Underground', swatch: 'oklch(0.5315 0.0694 156.19)' },
  { value: 'rose-garden', name: 'Rose Garden', swatch: 'oklch(0.5827 0.2418 12.23)' },
  { value: 'lake-view', name: 'Lake View', swatch: 'oklch(0.765 0.177 163.22)' },
  { value: 'sunset-glow', name: 'Sunset Glow', swatch: 'oklch(0.5827 0.2187 36.98)' },
  { value: 'forest-whisper', name: 'Forest Whisper', swatch: 'oklch(0.5276 0.1072 182.22)' },
  { value: 'lavender-dream', name: 'Lavender Dream', swatch: 'oklch(0.71 0.16 293.54)' },
] as const;

export type ThemePreset = (typeof THEME_PRESETS)[number]['value'];

/**
 * NAFIS — the walkthrough's own palette, and the only preset whose values come
 * from a reference rather than from the shadcn kit.
 *
 * It was Ocean Breeze, which was a kit preset: the console shipped in a hue
 * nobody had chosen, because each kit preset defines its own primary AND its own
 * typeface. Against the mock stakeholders were shown, that read as "the blue is
 * different, the fonts are different" — accurate, and not a decision anyone had
 * made.
 *
 * Changing this changes what a first-time visitor sees; it does NOT change
 * anyone who has already chosen a theme (their choice is in localStorage).
 */
export const DEFAULT_PRESET: ThemePreset = 'nafis';

/** Light / dark / follow-the-OS. Orthogonal to the preset — every preset has both. */
export type ThemeMode = 'light' | 'dark' | 'system';

export const DEFAULT_MODE: ThemeMode = 'system';

/** Shared with the pre-paint script in index.html — keep the strings in sync. */
export const STORAGE_KEYS = {
  mode: 'nafis-theme-mode',
  preset: 'nafis-theme-preset',
} as const;

export function isThemePreset(value: unknown): value is ThemePreset {
  return THEME_PRESETS.some((t) => t.value === value);
}

export function isThemeMode(value: unknown): value is ThemeMode {
  return value === 'light' || value === 'dark' || value === 'system';
}

export function presetName(value: ThemePreset): string {
  return THEME_PRESETS.find((t) => t.value === value)?.name ?? value;
}
